'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import RemixIcon from 'helpers/RemixIcon/RemixIcon';

interface DatePickerFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;

  // Standalone support (non-Formik)
  value?: string; // stored as DD/MM/YYYY
  onChange?: (value: string) => void;
  onBlur?: () => void;

  // Wrapper props
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
  hideLabel?: boolean;
  isStandalone?: boolean;

  /**
   * Optional: DOB mode
   * - If true and no maxDate configured, defaults maxDate to "today"
   */
  dobMode?: boolean;
}

/** ========= date helpers (safe parsing) ========= */

const getTodayISO = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toISODate = (val?: string): string | null => {
  if (!val) return null;

  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

  // DD/MM/YYYY
  const m = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;

  const [, dd, mm, yyyy] = m;
  const iso = `${yyyy}-${mm}-${dd}`;

  // Validate real date
  const dt = new Date(`${iso}T00:00:00`);
  if (
    dt.getFullYear() === Number(yyyy) &&
    dt.getMonth() + 1 === Number(mm) &&
    dt.getDate() === Number(dd)
  ) {
    return iso;
  }
  return null;
};

const isoToDDMMYYYY = (iso: string): string => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, yyyy, mm, dd] = m;
  return `${dd}/${mm}/${yyyy}`;
};

const clampISO = (iso: string, minISO?: string | null, maxISO?: string | null): string => {
  if (minISO && iso < minISO) return minISO;
  if (maxISO && iso > maxISO) return maxISO;
  return iso;
};

const isoToDate = (iso?: string | null): Date | undefined => {
  if (!iso) return undefined;
  const dt = new Date(`${iso}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? undefined : dt;
};

/** ========= component ========= */

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  field,
  hasError = false,
  value,
  onChange,
  onBlur,
  errors,
  touched,
  hideLabel,
  isStandalone = false,
  dobMode = false,
}) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;
  const placeholder = fieldConfig?.placeholderText?.value || 'DD/MM/YYYY';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawMinDate = (fieldConfig as any)?.minDate?.value;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawMaxDate = (fieldConfig as any)?.maxDate?.value;

  // Resolve today special value
  const resolvedMin = rawMinDate === 'today' ? getTodayISO() : rawMinDate;
  const resolvedMaxRaw =
    rawMaxDate === 'today' ? getTodayISO() : rawMaxDate || (dobMode ? 'today' : undefined);
  const resolvedMax = resolvedMaxRaw === 'today' ? getTodayISO() : resolvedMaxRaw;

  const minISO = toISODate(resolvedMin);
  const maxISO = toISODate(resolvedMax);

  const fieldState = disabled ? 'disabled' : hasError ? 'error' : 'default';

  // Popover state
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Outside click close
  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current?.contains(t) ||
        triggerRef.current?.contains(t) ||
        inputRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Escape close
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Focus into popover
  useEffect(() => {
    if (!open) return;
    popoverRef.current
      ?.querySelector<HTMLElement>('button, [tabindex]:not([tabindex="-1"])')
      ?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || !popoverRef.current) return;

    const injectDropdownIcons = () => {
      // Find the dropdowns container
      const dropdownsContainer = popoverRef.current?.querySelector('.rdp-month_caption');
      if (!dropdownsContainer) return;

      // Find all select elements within the dropdowns container
      const selects = dropdownsContainer.querySelectorAll('select');

      // First select is month, second is year
      const monthSelect = selects[0];
      const yearSelect = selects[1];

      // Helper function to inject icon next to a select element
      const injectIcon = (selectElement: HTMLSelectElement) => {
        // Check if icon already exists
        const wrapper = selectElement.parentElement;
        if (!wrapper || wrapper.querySelector('.custom-dropdown-remix-icon')) {
          return;
        }

        // Ensure parent has relative positioning
        if (
          wrapper &&
          !['relative', 'absolute', 'fixed'].includes(getComputedStyle(wrapper).position)
        ) {
          wrapper.style.position = 'relative';
        }

        // Create icon container
        const iconContainer = document.createElement('div');
        iconContainer.className =
          'custom-dropdown-remix-icon absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-component-button-tertiary-icon';

        // Render RemixIcon using React
        const root = createRoot(iconContainer);
        root.render(<RemixIcon name="arrow-down-s-line" />);

        wrapper.appendChild(iconContainer);
      };

      if (monthSelect) {
        injectIcon(monthSelect);
      }
      if (yearSelect) {
        injectIcon(yearSelect);
      }
    };

    // Small delay to ensure DayPicker has rendered
    const timer = setTimeout(injectDropdownIcons, 0);
    return () => clearTimeout(timer);
  }, [open]);

  const currentValue: string | undefined = value;
  const setCurrentValue = (next: string) => {
    if (isStandalone) onChange?.(next);
  };

  const handleBlur = () => {
    if (isStandalone) onBlur?.();
  };

  const selectedISO = toISODate(currentValue);
  const selectedDate = isoToDate(selectedISO);

  if (!fieldName) return null;

  // Get today's date for endMonth and max date constraint
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Ensure maxDate is always today or earlier (unless explicitly set to a past date)
  const effectiveMaxISO = maxISO || getTodayISO();
  const effectiveMaxDateObj = isoToDate(effectiveMaxISO);

  // Use today or the configured maxDate, whichever is earlier
  const finalMaxDate =
    effectiveMaxDateObj && effectiveMaxDateObj > today ? today : effectiveMaxDateObj || today;

  const commonInputProps = {
    name: fieldName,
    id: fieldName,
    disabled,
    placeholder,
    'data-automation': `datePickerField-${fieldName}`,
    'aria-required': required || undefined,
    'aria-invalid': hasError || undefined,
  } as const;

  const onSelectDate = (date: Date | undefined) => {
    if (!date) return;

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const pickedISO = `${yyyy}-${mm}-${dd}`;

    // Clamp to finalMaxDate (today or configured max, whichever is earlier)
    const clamped = clampISO(pickedISO, minISO, finalMaxDate.toISOString().split('T')[0]);
    setCurrentValue(isoToDDMMYYYY(clamped));
    setOpen(false);

    // nice UX: return focus to input
    inputRef.current?.focus();
  };

  // Disable outside range - always disable dates after today
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const disabledRules: any[] = [];
  const minDateObj = isoToDate(minISO);
  if (minDateObj) disabledRules.push({ before: minDateObj });
  disabledRules.push({ after: today });
  const formatWeekdayName = (date: Date): string => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[date.getDay()];
  };
  // DayPicker classNames for Tailwind CSS styling
  const dayPickerClassNames = {
    root: 'rdp',
    caption: 'w-full flex justify-start gap-x-spacing-spacing-8',
    month: ['w-full', 'flex', 'flex-col', 'justify-start', 'gap-y-spacing-spacing-32'].join(' '),
    // Hide the caption label when using dropdowns
    caption_label: 'sr-only',
    dropdowns: 'w-full flex flex-row justify-start gap-x-spacing-spacing-8',
    // Style the actual select elements (applies to both month and year dropdowns)
    dropdown: [
      'min-w-[102px]',
      'px-spacing-spacing-16',
      'pr-10', // Extra right padding for custom icon
      'py-spacing-spacing-12',
      'border',
      'border-color-input-border',
      'rounded-[8px]',
      'bg-color-grayscale-white',
      'text-color-text-text',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'hover:border-color-input-border-hover',
      'focus:outline-none',
      'focus:ring-1',
      'focus:border-color-input-border-focused',
      'focus:ring-color-input-border-focused',
      'focus:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]',
      'cursor-pointer',
      'appearance-none', // Hide default select arrow
      '[&::-ms-expand]:hidden', // Hide IE/Edge default arrow
    ].join(' '),
    // Custom styling for month dropdown wrapper - add icon here
    months_dropdown: [
      'relative',
      'flex-1',
      'min-w-[140px]',
      '[&_select]:pr-10', // Ensure select has right padding
    ].join(' '),
    // Custom styling for year dropdown wrapper - add icon here
    years_dropdown: [
      'relative',
      'flex-1',
      'min-w-[100px]',
      '[&_select]:pr-10', // Ensure select has right padding
    ].join(' '),
    // Style the chevron icon (if react-day-picker renders it)
    weekday: [
      'w-12',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-semibold',
      'text-color-grayscale-600',
      'text-center',
    ].join(' '),
    // Day cells
    day: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-semibold',
      'text-color-core-color-1-Navy',
      'w-12',
      'text-center',
      'py-spacing-spacing-14',
      'rounded-[4px]',
      'hover:bg-color-core-color-4-skyblue-lightTint',
      'focus:outline-none',
      'focus:ring-1',
      'focus:ring-color-input-border-focused',
    ].join(' '),
    day_button: 'mx-auto',
    selected: ['bg-color-core-color-4-SkyBlue', 'hover:bg-color-core-color-4-SkyBlue'].join(' '),
    today: ['font-semibold', 'text-color-core-color-4-SkyBlue'].join(' '),
    disabled: [
      'text-color-grayscale-600',
      'cursor-not-allowed',
      'hover:bg-transparent',
      'opacity-50',
    ].join(' '),
  };
  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
      errors={errors}
      touched={touched}
      hideLabel={hideLabel}
    >
      {/* Input + calendar trigger */}
      <div className="relative flex items-center gap-x-spacing-spacing-8">
        <div
          className={`${formFieldStyles({ state: fieldState })} relative flex items-center justify-between gap-x-spacing-spacing-8`}
        >
          <input
            ref={inputRef}
            type="text"
            inputMode="none"
            autoComplete={dobMode ? 'bday' : 'off'}
            value={currentValue ?? ''}
            onBlur={handleBlur}
            {...commonInputProps}
            readOnly
            className="w-full focus:outline-none"
          />

          <span className={`${calendarButtonStyles({ state: fieldState })}`}>
            <button
              ref={triggerRef}
              type="button"
              disabled={disabled}
              aria-label="Choose date from calendar"
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={`${fieldName}-popover`}
              onClick={() => setOpen((v) => !v)}
            >
              <RemixIcon name="ri-calendar-line" />
            </button>
          </span>
        </div>

        {/* Popover */}
        {open ? (
          <div
            id={`${fieldName}-popover`}
            ref={popoverRef}
            role="dialog"
            aria-modal="false"
            aria-label="Date picker"
            className={popoverStyles()}
          >
            <div className={popoverContainerStyles()}>
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={onSelectDate}
                captionLayout="dropdown"
                startMonth={new Date(1900, 0)}
                endMonth={today}
                disabled={disabledRules.length ? disabledRules : undefined}
                hideNavigation={true}
                classNames={dayPickerClassNames}
                formatters={{
                  formatWeekdayName: formatWeekdayName,
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </FormFieldWrapper>
  );
};

export default DatePickerField;

const formFieldStyles = tv({
  base: [
    'w-full',
    'min-h-12',
    'px-spacing-spacing-16',
    'py-spacing-spacing-12',
    'border',
    'border-color-input-border',
    'rounded-[8px]',
    'bg-color-grayscale-white',
    'placeholder:text-color-text-text-secondary',
    'text-color-text-text',
    'hover:border-color-input-border-hover',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-typography-line-height-body-medium',
    'placeholder:italic',
    'font-normal',
    'disabled:text-color-text-text-disabled',
    'disabled:cursor-not-allowed',
    'focus:outline-none',
    'focus:ring-1',
    'focus:border-color-input-border-focused',
    'focus:ring-color-input-border-focused',
    'focus:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]',
  ],
  variants: {
    state: {
      default: '',
      error: [
        'border-color-text-text-critical',
        'focus:border-color-text-text-error',
        'bg-color-fill-error-light',
      ],
      disabled: ['bg-color-grayscale-gray-100', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const calendarButtonStyles = tv({
  base: ['shrink-0', 'h-6', 'w-fit', 'bg-color-grayscale-white'],
  variants: {
    state: {
      default: '',
      error: ['border-color-text-text-critical', 'bg-color-fill-error-light'],
      disabled: '',
    },
  },
  defaultVariants: { state: 'default' },
});

const popoverStyles = tv({
  base: ['absolute', 'left-0', 'top-[calc(100%+8px)]', 'z-50'],
});

const popoverContainerStyles = tv({
  base: [
    'w-full',
    'rounded-border-radius-radius-4',
    'bg-color-grayscale-white',
    'p-spacing-spacing-16',
    'max-w-[348px]',
    'shadow-lg',
  ],
});
